import useModal from '@area2k/use-modal';
import { faFileAlt, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { SubdirectoryArrowRight } from '@mui/icons-material';

import ConfirmDeleteItem from './ConfirmDeleteItem';
import InvoiceItemForm from './InvoiceItemForm';

import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import IconicButton from '@/components/IconicButton';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import Link from '@/elements/Link';
import { TableCell, TableRow } from '@/elements/Table';
import ROUTES from '@/routes/routes';
import { IconColor } from '@/styles/colors';
import type { InvoiceItem } from '@/types/graphql';
import { StripeInvoice } from '@/types/graphql';
import { NOT_AVAILABLE_TEXT } from '@/util/constants';
import { getInvoiceDescription } from '@/util/invoice';
import { centsToCurrency } from '@/util/number';

type Props = {
  items: unknown;
  invoice: unknown;
  editable: boolean;
};

const ItemsCard = ({ items, invoice, editable }: Props) => {
  const invoiceItems = items as InvoiceItem[];
  const invoiceData = invoice as StripeInvoice;
  const showOvertime = useFeatureValue(FEATURE_TOGGLE.InvoiceOvertime, false);

  const [showForm, hideForm] = useModal<InvoiceItem>((invoiceItem) => {
    const invoiceItemExits = Object.keys(invoiceItem).length > 0;
    return (
      <InvoiceItemForm
        hideModal={hideForm}
        invoice={invoiceData}
        invoiceItem={invoiceItemExits ? invoiceItem : undefined}
      />
    );
  });

  const [showConfirmDeleteModal, hideConfirmDeleteModal] =
    useModal<InvoiceItem>(
      (invoiceItem) => (
        <ConfirmDeleteItem
          hideModal={hideConfirmDeleteModal}
          invoiceId={invoiceData.id}
          invoiceItemId={invoiceItem.id}
        />
      ),
      [items]
    );

  const headerFields = [
    { name: 'Service Date', style: { width: '9%' } },
    { name: 'Order #' },
    { name: 'Address', style: { width: '16%' } },
    { name: 'Worker', style: { width: '13%' } },
    { name: 'Description', style: { width: '17%' } },
    { name: 'Net Time' },
    { name: 'Bill Rate' },
    { name: 'Tips' },
    { name: 'Total' },
  ];

  if (editable) {
    headerFields.push({ name: 'Action' });
  }

  const orderIdParserLink = (orderText: string | null) => {
    if (!orderText) {
      return NOT_AVAILABLE_TEXT;
    }

    if (orderText.includes('#')) {
      const { 1: orderId } = orderText.split('#');

      return (
        <Link to={`../../${ROUTES.orders}/${orderId}`}>{`#${orderId}`}</Link>
      );
    }

    return orderText;
  };

  const nextRowOverTime = (index: number) => {
    const nextRow = invoiceItems[index + 1];
    return !!(
      nextRow &&
      nextRow.isOvertime &&
      nextRow.timesheetId === invoiceItems[index].timesheetId
    );
  };

  const isPartialOvertimeRow = (index: number) => {
    const prevRow = invoiceItems[index - 1];
    return !!(
      prevRow && prevRow.timesheetId === invoiceItems[index].timesheetId
    );
  };

  const renderOverTimeRow = (item: InvoiceItem, index: number) => {
    const { order, amount, skill, netTime, jobId, billRate, isHoliday } = item;
    const orderId = order?.split('#')?.[1] || '';
    return (
      <TableRow
        key={`overtime-row-${index}`}
        css={{ backgroundColor: 'white !important' }}
        id={`overtime-row-${index}`}
      >
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell>
          <SubdirectoryArrowRight
            htmlColor={IconColor}
            id="overtime-icon"
            style={{ float: 'right' }}
          />
        </TableCell>
        <TableCell>
          {isHoliday ? 'HOLIDAY ' : ''} OVERTIME: {`${skill?.name || ''}`}
        </TableCell>
        <TableCell>
          {jobId && netTime ? (
            <Link to={`../../${ROUTES.orders}/${orderId}/jobs/${jobId}`}>
              {netTime}
            </Link>
          ) : (
            ''
          )}
        </TableCell>
        <TableCell>{billRate ? `$${centsToCurrency(billRate)}` : ''}</TableCell>
        <TableCell />
        <TableCell>${centsToCurrency(Number(amount))}</TableCell>
      </TableRow>
    );
  };

  const renderTableContent = () => {
    if (invoiceItems.length > 0) {
      return invoiceItems.map((item, index: number) => {
        const {
          order,
          date,
          address,
          amount,
          worker,
          netTime,
          jobId,
          billRate,
          tip = 0,
          isOvertime,
        } = item;
        const orderId = order?.split('#')?.[1] || '';
        const description = getInvoiceDescription(item, showOvertime);
        const isNextRowOverTime = nextRowOverTime(index);
        const overTimeRow = isOvertime && isPartialOvertimeRow(index);

        return showOvertime && overTimeRow ? (
          renderOverTimeRow(item, index)
        ) : (
          <TableRow
            key={`item-card-${index}`}
            css={{
              backgroundColor: 'white !important',
              boxShadow: isNextRowOverTime ? 'none' : '',
            }}
          >
            <TableCell>{date || NOT_AVAILABLE_TEXT}</TableCell>
            <TableCell>{orderIdParserLink(order)}</TableCell>
            <TableCell>{address || NOT_AVAILABLE_TEXT}</TableCell>
            <TableCell>
              {worker ? (
                <Link to={`../../${ROUTES.workers}/${worker.id}`}>
                  {worker.user?.firstName + ' ' + worker.user?.lastName}
                </Link>
              ) : (
                NOT_AVAILABLE_TEXT
              )}
            </TableCell>
            <TableCell>{description || NOT_AVAILABLE_TEXT}</TableCell>
            <TableCell>
              {jobId && netTime ? (
                <Link to={`../../${ROUTES.orders}/${orderId}/jobs/${jobId}`}>
                  {netTime}
                </Link>
              ) : (
                NOT_AVAILABLE_TEXT
              )}
            </TableCell>
            <TableCell>
              {billRate ? `$${centsToCurrency(billRate)}` : NOT_AVAILABLE_TEXT}
            </TableCell>
            <TableCell>{jobId ? `$${tip}` : NOT_AVAILABLE_TEXT}</TableCell>
            <TableCell>${centsToCurrency(Number(amount))}</TableCell>
            {editable && (
              <TableCell>
                <Stack>
                  <IconicButton
                    a11yLabel="Edit Invoice Item"
                    appearance="clear"
                    icon={faPen}
                    size="md"
                    onClick={() => showForm(item)}
                  />
                  <IconicButton
                    a11yLabel="Remove Invoice Item"
                    appearance="clear"
                    icon={faTrash}
                    size="md"
                    onClick={() => showConfirmDeleteModal(item)}
                  />
                </Stack>
              </TableCell>
            )}
          </TableRow>
        );
      });
    }

    return (
      <TableRow>
        <TableCell colSpan={headerFields.length}>
          <EmptyState
            icon={faFileAlt}
            text="No invoice items were found"
            title="No Invoices"
          />
        </TableCell>
      </TableRow>
    );
  };

  const editableCardAction = editable
    ? [{ a11yLabel: 'Add', label: 'Add', onAction: showForm }]
    : undefined;

  return (
    <Card actions={editableCardAction} title="Items">
      <Table>
        <TableHeader fields={headerFields} />
        <tbody>{renderTableContent()}</tbody>
      </Table>
    </Card>
  );
};

export default ItemsCard;
